import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { 
  Footprints,
  Loader2,
  User,
  Crown,
  Download,
  Upload,
  CloudUpload,
  Apple,
  Star,
  Info,
  Brain,
  Home,
  Pencil,
  Check,
  Cloud,
  Phone,
  Navigation,
  Users,
  Refrigerator,
  MapPin,
  Pill,
  Droplets,
  MessageCircle,
  Eye,
  EyeOff,
} from 'lucide-react';
// Feature components now in CategoryView
import IOSInstallGuide from './IOSInstallGuide';
// These components are now in CategoryView
import PaywallModal from './PaywallModal';
import { SyncIndicator } from './SyncIndicator';
import { useTheme } from './ThemeProvider';
import { useAuth } from '../contexts/AuthContext';
import { useSync } from '../contexts/SyncContext';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { toast } from 'sonner';
import api from '../services/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { motion, AnimatePresence } from 'framer-motion';
import GymFinder from './GymFinder';
import { useMenuOrder, useProfile, useSubscription, useGymFinder, useBodyData, useDataManagement } from '../hooks';

// Refactored components
import { MealtimesDialog, BodyInfoDialog, InvoicesDialog, PersonalInfoDialog, DataPrivacyDialog, ThemeDialog, SubscriptionDialog, PersonalDialogContent } from './personal';
import { GymFinderCard } from './gym';
import { HomeHeader, ContinueWalkingBanner } from './home';
import { CategoryView } from './views';
import { BottomNavBar } from './navigation';

// Page transitions now in PageWrapper component

// App Version - Update this when deploying new versions
const APP_VERSION = '1.0';

// Timer state key (must match WalkingTimer.js)
const TIMER_STATE_KEY = 'walking_timer_state';

// Check for saved walking session
const checkSavedWalkingSession = () => {
  try {
    const saved = localStorage.getItem(TIMER_STATE_KEY);
    if (saved) {
      const state = JSON.parse(saved);
      const savedTime = new Date(state.savedAt).getTime();
      const now = Date.now();
      const hoursSinceSave = (now - savedTime) / (1000 * 60 * 60);
      if (hoursSinceSave < 24 && state.phase !== 'complete') {
        return state;
      }
    }
  } catch (e) {
    console.error('Error checking saved walking session:', e);
  }
  return null;
};

export default function MainApp({ onBack, onGoToWalking, initialView, onViewShown }) {
  const [selectedCategory, setSelectedCategoryState] = useState(null);
  const { theme } = useTheme();
  const { user, isAuthenticated, logout, refreshUser } = useAuth();
  const { startSync, completeSync, failSync, createBackup } = useSync();
  
  // Custom setSelectedCategory that integrates with browser history
  const setSelectedCategory = useCallback((category) => {
    if (category) {
      // Push to history when navigating to a category
      window.history.pushState({ category }, '', `#${category}`);
    } else {
      // When going back to main, replace state
      window.history.replaceState({ category: null }, '', window.location.pathname);
    }
    setSelectedCategoryState(category);
  }, []);
  
  // Listen for browser back/forward button
  useEffect(() => {
    const handlePopState = (event) => {
      // When user presses back button, go to main page or previous category
      const category = event.state?.category || null;
      setSelectedCategoryState(category);
    };
    
    window.addEventListener('popstate', handlePopState);
    
    // Set initial state
    window.history.replaceState({ category: null }, '', window.location.pathname);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);
  
  // Saved walking session state
  const [savedWalkingSession, setSavedWalkingSession] = useState(() => checkSavedWalkingSession());
  
  // Check for saved walking session periodically
  useEffect(() => {
    const checkSession = () => {
      setSavedWalkingSession(checkSavedWalkingSession());
    };
    
    // Check on visibility change (user returns to tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkSession();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also check every 30 seconds
    const interval = setInterval(checkSession, 30000);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(interval);
    };
  }, []);
  
  // Use custom hooks for subscription and profile management
  const {
    subscriptionStatus,
    showPaywall,
    paywallTrigger,
    handlePremiumFeatureAccess,
    handleFeatureAccess,
    closePaywall,
    setShowPaywall,
  } = useSubscription(user);
  
  // Personal dialog state (controlled)
  const [personalDialogOpen, setPersonalDialogOpen] = useState(false);
  
  // Medications visibility toggle (persisted in localStorage)
  const [medicationsEnabled, setMedicationsEnabled] = useState(() => {
    const stored = localStorage.getItem('medications_enabled');
    return stored === null ? true : stored === 'true';
  });
  const handleMedicationsToggle = useCallback((val) => {
    setMedicationsEnabled(val);
    localStorage.setItem('medications_enabled', String(val));
    // Immediately persist to cloud to prevent race condition on refresh
    api.post('/sync/cloud', { data: { medications_enabled: String(val) } }).catch(() => {});
  }, []);

  // Re-sync medications state when cloud pull overwrites localStorage
  useEffect(() => {
    const handler = () => {
      const stored = localStorage.getItem('medications_enabled');
      setMedicationsEnabled(stored === null ? true : stored === 'true');
    };
    window.addEventListener('cloud-sync-pulled', handler);
    return () => window.removeEventListener('cloud-sync-pulled', handler);
  }, []);

  // Diabetes settings (persisted in localStorage)
  const [diabetesEnabled, setDiabetesEnabled] = useState(() => {
    return localStorage.getItem('diabetes_enabled') === 'true';
  });
  const [diabetesUnit, setDiabetesUnit] = useState(() => {
    return localStorage.getItem('diabetes_unit') || 'mmol/l';
  });
  const [diabetesInsulin, setDiabetesInsulin] = useState(() => {
    return localStorage.getItem('diabetes_insulin') === 'true';
  });
  const [diabetesType, setDiabetesType] = useState(() => {
    return localStorage.getItem('diabetes_type') || 'type2';
  });

  const handleDiabetesToggle = useCallback((val) => {
    setDiabetesEnabled(val);
    localStorage.setItem('diabetes_enabled', String(val));
    api.post('/sync/cloud', { data: { diabetes_enabled: String(val) } })
      .catch(() => {
        // Retry once after a short delay
        setTimeout(() => {
          api.post('/sync/cloud', { data: { diabetes_enabled: String(val) } }).catch(() => {});
        }, 2000);
      });
  }, []);
  const handleDiabetesUnit = useCallback((val) => {
    setDiabetesUnit(val);
    localStorage.setItem('diabetes_unit', val);
    api.post('/sync/cloud', { data: { diabetes_unit: val } }).catch(() => {});
  }, []);
  const handleDiabetesInsulin = useCallback((val) => {
    setDiabetesInsulin(val);
    localStorage.setItem('diabetes_insulin', String(val));
    api.post('/sync/cloud', { data: { diabetes_insulin: String(val) } })
      .catch(() => {
        setTimeout(() => {
          api.post('/sync/cloud', { data: { diabetes_insulin: String(val) } }).catch(() => {});
        }, 2000);
      });
  }, []);
  const handleDiabetesType = useCallback((val) => {
    setDiabetesType(val);
    localStorage.setItem('diabetes_type', val);
    api.post('/sync/cloud', { data: { diabetes_type: val } }).catch(() => {});
  }, []);

  // Re-sync diabetes state when cloud pull overwrites localStorage
  useEffect(() => {
    const handler = () => {
      setDiabetesEnabled(localStorage.getItem('diabetes_enabled') === 'true');
      setDiabetesUnit(localStorage.getItem('diabetes_unit') || 'mmol/l');
      setDiabetesInsulin(localStorage.getItem('diabetes_insulin') === 'true');
      setDiabetesType(localStorage.getItem('diabetes_type') || 'type2');
    };
    window.addEventListener('cloud-sync-pulled', handler);

    // Delayed fallback: re-read localStorage after cloud sync has had time to complete
    // Guards against race condition where cloud-sync-pulled fires before this listener
    const fallbackTimer = setTimeout(handler, 3000);

    return () => {
      window.removeEventListener('cloud-sync-pulled', handler);
      clearTimeout(fallbackTimer);
    };
  }, [isAuthenticated]);
  
  // Gym Finder hook
  const {
    showGymFinder, setShowGymFinder,
    searchingGyms, nearbyGyms,
    gymSearchQuery, setGymSearchQuery,
    expandedGymId, gymDetails, loadingGymDetails,
    searchGyms, findNearbyGyms, fetchGymDetails, getDirections,
  } = useGymFinder(api);
  
  // Menu order management hook
  const {
    menuOrder,
    isEditMode,
    setIsEditMode,
    draggedItem,
    dragOverItem,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  } = useMenuOrder();

  // Hidden items management
  const [hiddenItems, setHiddenItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem('gym_notebook_hidden_items') || '[]'); }
    catch { return []; }
  });
  const [isHideMode, setIsHideMode] = useState(false);

  const toggleHideItem = (itemId) => {
    setHiddenItems(prev => {
      const updated = prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId];
      localStorage.setItem('gym_notebook_hidden_items', JSON.stringify(updated));
      return updated;
    });
  };
  
  // Profile management hook
  const {
    editingProfile,
    profileName,
    setProfileName,
    profileEmail,
    profileCity,
    setProfileCity,
    profileCountry,
    setProfileCountry,
    newPassword,
    setNewPassword,
    confirmPassword,
    setConfirmPassword,
    savingProfile,
    startEditing: startEditingProfile,
    cancelEditing: cancelEditingProfile,
    saveProfile,
  } = useProfile(user, refreshUser);
  
  // Handle logout
  const handleLogout = async () => {
    await logout();
    onBack();
  };
  
  // Handle initial view from parent
  useEffect(() => {
    if (initialView) {
      setSelectedCategory(initialView);
      if (onViewShown) onViewShown();
    }
  }, [initialView, onViewShown]);
  
  // Body data hook
  const {
    bodyDataLoaded,
    currentBodyweight, setCurrentBodyweight,
    newBodyweight, setNewBodyweight,
    weightUnit, setWeightUnit,
    bodyHeight, setBodyHeight,
    heightUnit, setHeightUnit,
    userAge, setUserAge,
    userGender, setUserGender,
    waistMeasurement, setWaistMeasurement,
    waistUnit, setWaistUnit,
    weightGoal, setWeightGoal,
    weightGoalStartDate, setWeightGoalStartDate,
    showGoalInfo, setShowGoalInfo,
    bodyMeasurements, setBodyMeasurements,
    measurementUnit, setMeasurementUnit,
    strideLength, setStrideLength,
    handleUpdateBodyweight,
    handleSaveMeasurements,
  } = useBodyData({ isAuthenticated, startSync, completeSync, failSync });
  
  // Update notification state
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [newVersion, setNewVersion] = useState(null);
  
  // Data management hook (backup, GDPR, mealtimes, payments)
  const {
    showRestoreBackup, setShowRestoreBackup,
    backupList, loadingBackups,
    restoringBackup, handleRestoreBackup, handleRestoreFromS3,
    downloadingData, emailingPolicy, deletingData,
    dataSummary, loadingDataSummary,
    loadDataSummary, handleDownloadAllData,
    handleDownloadGdprPdf, handleEmailGdprPolicy,
    handleDeleteAllData,
    userMealTimes, setUserMealTimes,
    customMealName, setCustomMealName,
    savingMealTimes, setSavingMealTimes,
    paymentHistory, loadingHistory,
    downloadingInvoice, loadPaymentHistory,
    handleDownloadInvoice,
  } = useDataManagement({ api, isAuthenticated, createBackup });

  // Detect iOS devices
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  
  // Version tracking
  useEffect(() => {
    localStorage.setItem('gym_notebook_app_version', APP_VERSION);
  }, []);
  
  // Handle update action
  const handleUpdate = () => {
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    window.location.reload(true);
  };
  
  // Automatically extend trial to 14 days for users who signed up during 7-day trial period
  useEffect(() => {
    const extendTrialIfNeeded = async () => {
      if (!isAuthenticated || !user) return;
      
      // Only try to extend if user is in trial status and has less than 14 days remaining
      if (user.subscription_status === 'trial' && subscriptionStatus.daysRemaining < 14) {
        try {
          const response = await api.post('/auth/extend-trial');
          if (response.extended) {
            // Refresh user data to get the new trial end date
            await refreshUser();
            toast.success('Trial extended to 14 days!');
          }
        } catch (e) {
          console.log('Trial extension check failed:', e.message);
        }
      }
    };
    
    // Run once on mount with a delay
    const timer = setTimeout(extendTrialIfNeeded, 1500);
    return () => clearTimeout(timer);
  }, [isAuthenticated, user?.user_id]); // Only run when user changes
  
  // If Nutrition Tracker is selected
  // Render category views using extracted component
  const categoryView = (
    <CategoryView
      selectedCategory={selectedCategory}
      onBack={() => setSelectedCategory(null)}
      isExpired={subscriptionStatus.isExpired}
      onNavigate={(page) => {
        if (page === 'personal') {
          setPersonalDialogOpen(true);
        } else {
          handleFeatureAccess(page, () => setSelectedCategory(page));
        }
      }}
      customMealTimes={userMealTimes.filter(m => m.enabled)}
      onGoToWalking={onGoToWalking}
    />
  );

  // Detect desktop (lg: 1024px+)
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // --- Shared helper: menu item definitions ---
  const getMenuItems = () => ({
    nutrition: {
      id: 'nutrition',
      label: 'Nutrition Master',
      description: 'Track calories & macros',
      icon: Apple,
      color: 'orange',
      iconAnimation: 'icon-bite',
      onClick: () => handleFeatureAccess('nutrition', () => setSelectedCategory('nutrition')),
      testId: 'nutrition-tracker-btn',
      badge: subscriptionStatus.isExpired ? (
        <span className="absolute top-2 right-2 text-[10px] bg-amber-500/30 text-amber-300 px-2 py-0.5 rounded-full flex items-center gap-1">
          <Crown className="h-3 w-3" /> Subscribe
        </span>
      ) : null
    },
    walking: {
      id: 'walking',
      label: 'Walking Shark',
      description: 'Track steps & distance',
      icon: Footprints,
      color: 'blue',
      iconAnimation: 'icon-walk',
      onClick: () => handleFeatureAccess('walking', () => setSelectedCategory('walking')),
      testId: 'walking-dashboard-btn',
      badge: subscriptionStatus.isExpired ? (
        <span className="absolute top-2 right-2 text-[10px] bg-amber-500/30 text-amber-300 px-2 py-0.5 rounded-full flex items-center gap-1">
          <Crown className="h-3 w-3" /> Subscribe
        </span>
      ) : null
    },
    aiplanner: {
      id: 'aiplanner',
      label: 'AI Planner',
      description: 'Personalized diet & walking plans',
      icon: Brain,
      color: 'purple',
      iconAnimation: 'icon-think',
      onClick: () => handleFeatureAccess('ai_planner', () => setSelectedCategory('aiplanner')),
      testId: 'ai-planner-btn',
      isPremium: true,
      badge: subscriptionStatus.isExpired ? (
        <span className="absolute top-2 right-2 text-[10px] bg-amber-500/30 text-amber-300 px-2 py-0.5 rounded-full flex items-center gap-1">
          <Crown className="h-3 w-3" /> Subscribe
        </span>
      ) : (
        <span className="absolute top-2 right-2 text-[10px] bg-purple-500/30 text-purple-300 px-2 py-0.5 rounded-full">
          Powered by AI
        </span>
      )
    },
    fridge: {
      id: 'fridge',
      label: 'FridgeOracle',
      description: 'Scan fridge & rescue recipes',
      icon: Refrigerator,
      color: 'cyan',
      iconAnimation: 'icon-pulse',
      onClick: () => handleFeatureAccess('fridge', () => setSelectedCategory('fridge')),
      testId: 'fridge-oracle-btn',
      badge: (
        <span className="absolute top-2 right-2 text-[10px] bg-cyan-500/30 text-cyan-300 px-2 py-0.5 rounded-full">
          AI Vision
        </span>
      )
    },
    community: {
      id: 'community',
      label: 'Community Lion',
      description: 'Share & compete with friends',
      icon: Users,
      color: 'green',
      iconAnimation: 'icon-walk',
      onClick: () => handleFeatureAccess('community', () => setSelectedCategory('community')),
      testId: 'community-btn',
    },
    medications: {
      id: 'medications',
      label: 'Medications HQ',
      description: 'Track meds & get reminders',
      icon: Pill,
      color: 'teal',
      iconAnimation: 'icon-float',
      onClick: () => handleFeatureAccess('medications', () => setSelectedCategory('medications')),
      testId: 'medications-btn',
    },
    diabetes: {
      id: 'diabetes',
      label: 'Diabetes Diary',
      description: 'Blood sugar & insulin tracking',
      icon: Droplets,
      color: 'rose',
      iconAnimation: 'icon-pulse',
      onClick: () => handleFeatureAccess('diabetes', () => setSelectedCategory('diabetes')),
      testId: 'diabetes-diary-btn',
    },
    healthcoach: {
      id: 'healthcoach',
      label: 'Health Coach',
      description: 'AI-powered health Q&A',
      icon: MessageCircle,
      color: 'pink',
      iconAnimation: 'icon-think',
      onClick: () => handleFeatureAccess('healthcoach', () => setSelectedCategory('healthcoach')),
      testId: 'health-coach-btn',
      isPremium: true,
      badge: (
        <span className="absolute top-2 right-2 text-[10px] bg-pink-500/30 text-pink-300 px-2 py-0.5 rounded-full">
          Voice AI
        </span>
      ),
    }
  });

  // --- Shared helper: color classes ---
  const getColorClasses = () => ({
    green: {
      bg: 'from-emerald-500/10 to-green-600/10',
      border: 'border-emerald-500/30 hover:border-emerald-400/50',
      shadow: 'hover:shadow-emerald-500/20',
      iconGradient: 'from-emerald-500 to-green-600',
      iconShadow: 'shadow-emerald-500/30',
      textHover: 'group-hover:text-emerald-400'
    },
    orange: {
      bg: 'from-amber-500/10 to-orange-600/10',
      border: 'border-amber-500/30 hover:border-amber-400/50',
      shadow: 'hover:shadow-amber-500/20',
      iconGradient: 'from-amber-500 to-orange-600',
      iconShadow: 'shadow-amber-500/30',
      textHover: 'group-hover:text-amber-400'
    },
    blue: {
      bg: 'from-blue-500/10 to-cyan-600/10',
      border: 'border-blue-500/30 hover:border-blue-400/50',
      shadow: 'hover:shadow-blue-500/20',
      iconGradient: 'from-blue-500 to-cyan-600',
      iconShadow: 'shadow-blue-500/30',
      textHover: 'group-hover:text-blue-400'
    },
    purple: {
      bg: 'from-violet-500/10 to-purple-600/10',
      border: 'border-violet-500/30 hover:border-violet-400/50',
      shadow: 'hover:shadow-violet-500/20',
      iconGradient: 'from-violet-500 to-purple-600',
      iconShadow: 'shadow-violet-500/30',
      textHover: 'group-hover:text-violet-400'
    },
    cyan: {
      bg: 'from-cyan-500/10 to-sky-600/10',
      border: 'border-cyan-500/30 hover:border-cyan-400/50',
      shadow: 'hover:shadow-cyan-500/20',
      iconGradient: 'from-cyan-500 to-sky-600',
      iconShadow: 'shadow-cyan-500/30',
      textHover: 'group-hover:text-cyan-400'
    },
    teal: {
      bg: 'from-teal-500/10 to-emerald-600/10',
      border: 'border-teal-500/30 hover:border-teal-400/50',
      shadow: 'hover:shadow-teal-500/20',
      iconGradient: 'from-teal-500 to-emerald-600',
      iconShadow: 'shadow-teal-500/30',
      textHover: 'group-hover:text-teal-400'
    },
    rose: {
      bg: 'from-rose-500/10 to-red-600/10',
      border: 'border-rose-500/30 hover:border-rose-400/50',
      shadow: 'hover:shadow-rose-500/20',
      iconGradient: 'from-rose-500 to-red-600',
      iconShadow: 'shadow-rose-500/30',
      textHover: 'group-hover:text-rose-400'
    },
    pink: {
      bg: 'from-pink-500/10 to-fuchsia-600/10',
      border: 'border-pink-500/30 hover:border-pink-400/50',
      shadow: 'hover:shadow-pink-500/20',
      iconGradient: 'from-pink-500 to-fuchsia-600',
      iconShadow: 'shadow-pink-500/30',
      textHover: 'group-hover:text-pink-400'
    }
  });

  // --- Shared JSX: Personal Dialog ---
  const personalDialog = (
    <Dialog open={personalDialogOpen} onOpenChange={setPersonalDialogOpen}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-sm max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
              <User className="h-4 w-4 text-white" />
            </div>
            Personal
            <span className="text-xs text-slate-500 ml-auto">v{APP_VERSION}</span>
          </DialogTitle>
        </DialogHeader>
        <PersonalDialogContent
          isAuthenticated={isAuthenticated}
          profileName={profileName}
          setProfileName={setProfileName}
          profileEmail={profileEmail}
          profileCity={profileCity}
          setProfileCity={setProfileCity}
          profileCountry={profileCountry}
          setProfileCountry={setProfileCountry}
          savingProfile={savingProfile}
          saveProfile={saveProfile}
          userMealTimes={userMealTimes}
          setUserMealTimes={setUserMealTimes}
          customMealName={customMealName}
          setCustomMealName={setCustomMealName}
          savingMealTimes={savingMealTimes}
          setSavingMealTimes={setSavingMealTimes}
          newBodyweight={newBodyweight}
          setNewBodyweight={setNewBodyweight}
          weightUnit={weightUnit}
          setWeightUnit={setWeightUnit}
          waistMeasurement={waistMeasurement}
          setWaistMeasurement={setWaistMeasurement}
          waistUnit={waistUnit}
          setWaistUnit={setWaistUnit}
          bodyHeight={bodyHeight}
          setBodyHeight={setBodyHeight}
          heightUnit={heightUnit}
          setHeightUnit={setHeightUnit}
          userAge={userAge}
          setUserAge={setUserAge}
          userGender={userGender}
          setUserGender={setUserGender}
          strideLength={strideLength}
          setStrideLength={setStrideLength}
          medicationsEnabled={medicationsEnabled}
          setMedicationsEnabled={handleMedicationsToggle}
          weightGoal={weightGoal}
          setWeightGoal={setWeightGoal}
          weightGoalStartDate={weightGoalStartDate}
          setWeightGoalStartDate={setWeightGoalStartDate}
          diabetesEnabled={diabetesEnabled}
          setDiabetesEnabled={handleDiabetesToggle}
          diabetesUnit={diabetesUnit}
          setDiabetesUnit={handleDiabetesUnit}
          diabetesInsulin={diabetesInsulin}
          setDiabetesInsulin={handleDiabetesInsulin}
          diabetesType={diabetesType}
          setDiabetesType={handleDiabetesType}
          onUpdateBodyweight={handleUpdateBodyweight}
          paymentHistory={paymentHistory}
          loadingHistory={loadingHistory}
          downloadingInvoice={downloadingInvoice}
          onLoadHistory={loadPaymentHistory}
          onDownloadInvoice={handleDownloadInvoice}
          loadingDataSummary={loadingDataSummary}
          dataSummary={dataSummary}
          onLoadDataSummary={loadDataSummary}
          downloadingData={downloadingData}
          onDownloadAllData={handleDownloadAllData}
          emailingPolicy={emailingPolicy}
          onEmailGdprPolicy={handleEmailGdprPolicy}
          onDownloadGdprPdf={handleDownloadGdprPdf}
          deletingData={deletingData}
          onDeleteAllData={handleDeleteAllData}
          onNavigateWearables={() => { setPersonalDialogOpen(false); handlePremiumFeatureAccess('wearables', () => setSelectedCategory('wearables')); }}
          onLogout={handleLogout}
          isExpired={subscriptionStatus.isExpired}
        />
      </DialogContent>
    </Dialog>
  );


  // Persistent bottom nav bar - always rendered regardless of view
  const bottomNavBar = (
    <BottomNavBar
      currentPage={selectedCategory}
      onNavigate={(page) => {
        if (page === null) {
          setSelectedCategory(null);
        } else {
          handleFeatureAccess(page, () => setSelectedCategory(page));
        }
      }}
      isExpired={subscriptionStatus.isExpired}
      showPersonalDialog={() => setPersonalDialogOpen(true)}
      isDesktop={isDesktop && !!selectedCategory}
    />
  );

  // If a category is selected, show its view
  if (selectedCategory && categoryView) {
    // On desktop (lg+): split panel with cards on left, content on right
    if (isDesktop) {
      return (
        <>
          <div className="split-panel">
            {/* LEFT: Dashboard cards in single column */}
            <div className="split-panel-cards bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
              <div className="px-4 pt-4 pb-2">
                <HomeHeader />
                <div className="mt-16 space-y-3">
                  {menuOrder.map((itemId) => {
                    if (itemId === 'medications' && !medicationsEnabled) return null;
                    if (itemId === 'diabetes' && !diabetesEnabled) return null;
                    if (itemId === 'healthcoach' && !medicationsEnabled && !diabetesEnabled) return null;
                    const menuItems = getMenuItems();
                    const item = menuItems[itemId];
                    if (!item) return null;
                    const IconComponent = item.icon;
                    const colors = getColorClasses()[item.color];
                    const isActive = selectedCategory === itemId;
                    return (
                      <button
                        key={itemId}
                        onClick={item.onClick}
                        data-testid={`split-${item.testId}`}
                        className={`w-full group bg-gradient-to-br ${colors.bg} backdrop-blur-sm border-2 ${isActive ? 'border-emerald-400 ring-2 ring-emerald-400/30' : colors.border} rounded-3xl p-5 transition-all duration-300 hover:scale-[1.01] hover:shadow-xl ${colors.shadow} relative`}
                      >
                        {item.badge}
                        <div className="flex items-center gap-4">
                          <div className={`w-14 h-14 bg-gradient-to-br ${colors.iconGradient} rounded-2xl flex items-center justify-center shadow-lg ${colors.iconShadow}`}>
                            <IconComponent className={`w-7 h-7 text-white ${item.iconAnimation || ''}`} />
                          </div>
                          <div className="text-left">
                            <h3 className={`text-lg font-heading font-bold text-white ${colors.textHover} transition-colors`}>
                              {item.label}
                            </h3>
                            <p className="text-slate-400 text-sm">{item.description}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            {/* RIGHT: Category content */}
            <div className="split-panel-content">
              {categoryView}
            </div>
          </div>
          {bottomNavBar}
          {personalDialog}
      </>
      );
    }
    // Mobile: just the category view
    return (
      <>
        {categoryView}
        {bottomNavBar}
        {personalDialog}
      </>
    );
  }

  // Main Home View with bottom taskbar
  return (
    <div className={`min-h-screen bg-gradient-to-br ${theme.gradient} flex flex-col overflow-hidden`}>
      {/* iOS Install Guide */}
      <IOSInstallGuide />
      
      {/* Update Available Banner */}
      {updateAvailable && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-gradient-to-r from-green-600 to-blue-500 text-white px-4 py-3 shadow-lg animate-pulse">
          <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-lg">🆕</span>
              <span className="font-medium">New version available! (v{newVersion})</span>
            </div>
            <button
              onClick={handleUpdate}
              className="px-4 py-1.5 bg-white text-green-600 rounded-full font-semibold text-sm hover:bg-green-50 transition-colors"
            >
              Update Now
            </button>
          </div>
        </div>
      )}
      
      {/* Header */}
      <HomeHeader />

      {/* Main Content */}
      <main className="dashboard-content flex-1 container mx-auto px-4 py-6 pb-36 pt-20 overflow-y-auto">
        <div className="dashboard-content-inner max-w-4xl mx-auto">
          {/* Continue Walking Session Banner */}
          {!isEditMode && !isHideMode && (
            <ContinueWalkingBanner 
              savedWalkingSession={savedWalkingSession}
              onContinue={() => handleFeatureAccess('walking', () => onGoToWalking())}
            />
          )}
          
          {/* Edit Mode Toggle */}
          <div className="flex justify-end gap-2 mb-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setIsHideMode(!isHideMode); if (isEditMode) setIsEditMode(false); }}
              className={`text-xs ${isHideMode ? 'text-amber-400 bg-amber-500/20' : 'text-slate-400'}`}
              data-testid="hide-mode-btn"
            >
              {isHideMode ? (
                <>
                  <Check className="h-4 w-4 mr-1" />
                  Done
                </>
              ) : (
                <>
                  <EyeOff className="h-4 w-4 mr-1" />
                  Hide
                </>
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setIsEditMode(!isEditMode); if (isHideMode) setIsHideMode(false); }}
              className={`text-xs ${isEditMode ? 'text-green-400 bg-green-500/20' : 'text-slate-400'}`}
            >
              {isEditMode ? (
                <>
                  <Check className="h-4 w-4 mr-1" />
                  Done
                </>
              ) : (
                <>
                  <Pencil className="h-4 w-4 mr-1" />
                  Reorder
                </>
              )}
            </Button>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Render menu items in saved order */}
            {menuOrder.map((itemId) => {
              if (itemId === 'medications' && !medicationsEnabled) return null;
              if (itemId === 'diabetes' && !diabetesEnabled) return null;
              if (itemId === 'healthcoach' && !medicationsEnabled && !diabetesEnabled) return null;
              const menuItems = getMenuItems();
              const item = menuItems[itemId];
              if (!item) return null;

              const isHidden = hiddenItems.includes(itemId);
              // In normal mode, skip hidden items
              if (isHidden && !isHideMode) return null;
              
              const IconComponent = item.icon;
              const colors = getColorClasses()[item.color];
              const isDragging = draggedItem === itemId;
              const isDragOver = dragOverItem === itemId;
              
              return (
                <div
                  key={itemId}
                  data-menu-id={itemId}
                  draggable={isEditMode}
                  onDragStart={(e) => handleDragStart(e, itemId)}
                  onDragOver={(e) => handleDragOver(e, itemId)}
                  onDrop={(e) => handleDrop(e, itemId)}
                  onDragEnd={() => {}}
                  onTouchStart={(e) => handleTouchStart(e, itemId)}
                  onTouchMove={(e) => handleTouchMove(e, itemId)}
                  onTouchEnd={handleTouchEnd}
                  className={`col-span-2 lg:col-span-1 ${isDragging ? 'opacity-50' : ''} ${isDragOver ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-slate-900' : ''} ${isHidden ? 'opacity-40' : ''} transition-all`}
                >
                  <button
                    onClick={isEditMode ? undefined : isHideMode ? () => toggleHideItem(itemId) : item.onClick}
                    data-testid={item.testId}
                    className={`w-full group bg-gradient-to-br ${colors.bg} backdrop-blur-sm border-2 ${colors.border} rounded-3xl p-5 transition-all duration-300 ${isEditMode ? 'cursor-grab active:cursor-grabbing' : isHideMode ? 'cursor-pointer' : 'hover:scale-[1.02] hover:-translate-y-1'} hover:shadow-xl ${colors.shadow} relative`}
                  >
                    {/* Hide mode toggle icon */}
                    {isHideMode && (
                      <div className="absolute top-3 right-3 z-10">
                        {isHidden ? (
                          <EyeOff className="h-5 w-5 text-red-400" />
                        ) : (
                          <Eye className="h-5 w-5 text-green-400" />
                        )}
                      </div>
                    )}
                    {item.badge}
                    <div className="flex items-center gap-4">
                      {/* Drag handle in edit mode */}
                      {isEditMode && (
                        <div className="flex flex-col gap-1 text-slate-500">
                          <div className="w-1.5 h-1.5 rounded-full bg-current"></div>
                          <div className="w-1.5 h-1.5 rounded-full bg-current"></div>
                          <div className="w-1.5 h-1.5 rounded-full bg-current"></div>
                        </div>
                      )}
                      <div className={`w-14 h-14 bg-gradient-to-br ${colors.iconGradient} rounded-2xl flex items-center justify-center shadow-lg ${colors.iconShadow}`}>
                        <IconComponent className={`w-7 h-7 text-white ${item.iconAnimation || ''}`} />
                      </div>
                      <div className="text-left">
                        <h3 className={`text-lg font-heading font-bold text-white ${colors.textHover} transition-colors`}>
                          {item.label}
                        </h3>
                        <p className="text-slate-400 text-sm">{item.description}</p>
                      </div>
                    </div>
                  </button>
                </div>
              );
            })}

            {/* Gym Finder - compact card, next to Community */}
            <div className="col-span-2 lg:col-span-1">
              <button
                onClick={() => setShowGymFinder(!showGymFinder)}
                data-testid="gym-finder-btn"
                className="w-full group bg-gradient-to-br from-cyan-500/10 to-blue-600/10 backdrop-blur-sm border-2 border-cyan-500/30 hover:border-cyan-400/50 rounded-3xl p-5 transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1 hover:shadow-xl hover:shadow-cyan-500/20 relative"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-cyan-500/30">
                    <MapPin className="w-7 h-7 text-white icon-pulse" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-lg font-heading font-bold text-white group-hover:text-cyan-400 transition-colors">
                      Gym Finder
                    </h3>
                    <p className="text-slate-400 text-sm">Find a gym nearby</p>
                  </div>
                </div>
              </button>
            </div>
          </div>
          
          {/* Gym Finder - expanded content below grid */}
          <GymFinderCard
            showButton={false}
            showGymFinder={showGymFinder}
            setShowGymFinder={setShowGymFinder}
            gymSearchQuery={gymSearchQuery}
            setGymSearchQuery={setGymSearchQuery}
            searchingGyms={searchingGyms}
            nearbyGyms={nearbyGyms}
            gymDetails={gymDetails}
            expandedGymId={expandedGymId}
            loadingGymDetails={loadingGymDetails}
            onSearchGyms={searchGyms}
            onFindNearbyGyms={findNearbyGyms}
            onFetchGymDetails={fetchGymDetails}
            onGetDirections={getDirections}
          />
        </div>
      </main>
      
      {/* Bottom Navigation Bar - persistent across all views */}
      {bottomNavBar}

      {/* Personal Dialog (state-controlled) */}
      {personalDialog}

      {/* Restore Backup Modal */}
      <Dialog open={showRestoreBackup} onOpenChange={setShowRestoreBackup}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Download className="h-5 w-5 text-blue-400" />
              Restore from Backup
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-sm text-slate-400">
              Select a backup date to restore your data. This will replace your current data with the backup.
            </p>
            
            {loadingBackups ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-blue-400" />
              </div>
            ) : backupList.length === 0 ? (
              <div className="text-center py-8">
                <CloudUpload className="h-12 w-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No backups found</p>
                <p className="text-slate-500 text-sm mt-1">Backups are created automatically daily</p>
              </div>
            ) : (
              <div className="space-y-2">
                {backupList.map((backup) => (
                  <div
                    key={backup.date}
                    className="p-3 bg-slate-700/50 rounded-lg flex items-center justify-between"
                  >
                    <div>
                      <p className="text-white font-medium">{backup.date}</p>
                      <p className="text-xs text-slate-400">
                        {backup.items?.photos || 0} photos
                      </p>
                    </div>
                    <Button
                      onClick={() => handleRestoreBackup(backup.date)}
                      disabled={restoringBackup}
                      size="sm"
                      className="bg-blue-500 hover:bg-blue-400"
                    >
                      {restoringBackup ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Restore'
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Trial Expiring Banner */}
      {isAuthenticated && subscriptionStatus.status === 'trial' && subscriptionStatus.daysRemaining <= 7 && subscriptionStatus.daysRemaining > 0 && !selectedCategory && (
        <div className="fixed bottom-20 left-4 right-4 z-40 bg-gradient-to-r from-amber-600/90 to-orange-600/90 backdrop-blur-sm rounded-lg p-3 shadow-lg border border-amber-500/30">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-amber-200" />
              <span className="text-white text-sm">
                <strong>{subscriptionStatus.daysRemaining} day{subscriptionStatus.daysRemaining !== 1 ? 's' : ''}</strong> left in your trial
              </span>
            </div>
            <Button 
              size="sm" 
              onClick={() => setShowPaywall(true)}
              className="bg-white/20 hover:bg-white/30 text-white text-xs"
            >
              Upgrade
            </Button>
          </div>
        </div>
      )}
      
      {/* Expired Trial Banner */}
      {isAuthenticated && subscriptionStatus.isExpired && !selectedCategory && (
        <div className="fixed bottom-20 left-4 right-4 z-40 bg-gradient-to-r from-red-600/90 to-orange-600/90 backdrop-blur-sm rounded-lg p-3 shadow-lg border border-red-500/30">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-red-200" />
              <span className="text-white text-sm">
                Your trial has ended. Subscribe to unlock all features.
              </span>
            </div>
            <Button 
              size="sm" 
              onClick={() => setShowPaywall(true)}
              className="bg-white/20 hover:bg-white/30 text-white text-xs"
            >
              Subscribe
            </Button>
          </div>
        </div>
      )}
      
      {/* Paywall Modal */}
      <PaywallModal
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        onSubscribe={() => {
          setShowPaywall(false);
          setSelectedCategory('subscription');
        }}
        daysRemaining={subscriptionStatus.daysRemaining}
        isExpired={subscriptionStatus.isExpired}
      />
    </div>
  );
}
